package ai.wagemix.web.components

import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMCardCornerVar
import ai.wagemix.web.theme.WMCardPaddingCompactVar
import ai.wagemix.web.theme.WMHairlineVar
import ai.wagemix.web.theme.WMIconSizeVar
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.theme.WMNoticeIconGapVar
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.LineStyle

/** Penpot `Wagemix / Notice`, property `Kind`. */
enum class WMNoticeKind {
    Info,
    /** Amber: data-quality notices, quota warnings. */
    Warning,
    Error,
    /** Teal: projected or AI-generated data. */
    AI
}

sealed interface WMNoticeKindMarker: ComponentKind

val WMNoticeStyle = CssStyle<WMNoticeKindMarker> {
    base {
        Modifier
            .fillMaxWidth()
            .gap(WMNoticeIconGapVar.value())
            .padding(WMCardPaddingCompactVar.value())
            .borderRadius(WMCardCornerVar.value())
    }
}

val WMInfoNoticeVariant = WMNoticeStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier.backgroundColor(colors.primaryContainer).color(colors.onPrimaryContainer)
            .border(WMHairlineVar.value(), LineStyle.Solid, colors.primaryContainer)
    }
}
val WMWarningNoticeVariant = WMNoticeStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier.backgroundColor(colors.tertiaryContainer).color(colors.onTertiaryContainer)
            .border(WMHairlineVar.value(), LineStyle.Solid, colors.tertiaryContainer)
    }
}
val WMErrorNoticeVariant = WMNoticeStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier.backgroundColor(colors.errorContainer).color(colors.onErrorContainer)
            .border(WMHairlineVar.value(), LineStyle.Solid, colors.errorContainer)
    }
}
val WMAINoticeVariant = WMNoticeStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier.backgroundColor(colors.secondaryContainer).color(colors.onSecondaryContainer)
            .border(WMHairlineVar.value(), LineStyle.Solid, colors.secondaryContainer)
    }
}

fun WMNoticeKind.variant(): CssStyleVariant<WMNoticeKindMarker> = when (this) {
    WMNoticeKind.Info -> WMInfoNoticeVariant
    WMNoticeKind.Warning -> WMWarningNoticeVariant
    WMNoticeKind.Error -> WMErrorNoticeVariant
    WMNoticeKind.AI -> WMAINoticeVariant
}

private fun WMNoticeKind.glyph(): WMIconGlyph = when (this) {
    WMNoticeKind.Info -> WMIconGlyph.Info
    WMNoticeKind.Warning -> WMIconGlyph.TriangleAlert
    WMNoticeKind.Error -> WMIconGlyph.TriangleAlert
    WMNoticeKind.AI -> WMIconGlyph.Sparkles
}

/**
 * Penpot `Wagemix / Notice`: an icon, a strong one-line [title], an optional [message] and an
 * optional [action] slot (a link or a small button). Errors and warnings use `role="alert"`.
 */
@Composable
fun WMNotice(
    title: String,
    modifier: Modifier = Modifier,
    kind: WMNoticeKind = WMNoticeKind.Info,
    message: String? = null,
    action: (@Composable RowScope.() -> Unit)? = null
) {
    val role = when (kind) {
        WMNoticeKind.Error, WMNoticeKind.Warning -> "alert"
        else -> "status"
    }
    Row(
        modifier = WMNoticeStyle.toModifier(kind.variant()).role(role).then(modifier),
        verticalAlignment = Alignment.Top
    ) {
        WMIcon(kind.glyph(), accessibilityString = null, modifier = Modifier.flexShrink(0), size = WMIconSizeVar.value())
        Column(modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value())) {
            SpanText(title, WMLabelStrongTextStyle.toModifier())
            message?.let { SpanText(it, WMBodySmallTextStyle.toModifier()) }
            action?.let { Row(modifier = Modifier.gap(WMTightGapVar.value()), verticalAlignment = Alignment.CenterVertically) { it() } }
        }
    }
}
